import { bodyPresent, type BodyJoint, type BodyPose } from './bodyPoseFrame.ts';

/** Кости по суставам `BodyPoseData`: корпус, руки до кончиков пальцев, таз (ног в кадре нет). */
export const SKELETON_BONES: readonly (readonly [BodyJoint, BodyJoint])[] = [
  ['leftShoulder', 'rightShoulder'],
  ['leftShoulder', 'leftElbow'],
  ['leftElbow', 'leftWrist'],
  ['leftWrist', 'leftIndex'],
  ['rightShoulder', 'rightElbow'],
  ['rightElbow', 'rightWrist'],
  ['rightWrist', 'rightIndex'],
  ['leftShoulder', 'leftHip'],
  ['rightShoulder', 'rightHip'],
  ['leftHip', 'rightHip'],
];

/** Над меню (106) и подложкой Sky Race, под курсором руки (107). */
const Z_INDEX = 106;
const COLOR = '80,220,255';

function drawPose(ctx: CanvasRenderingContext2D, pose: BodyPose, W: number, H: number): void {
  ctx.clearRect(0, 0, W, H);
  const lineW = Math.max(H * 0.008, 3);
  ctx.save();
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.lineWidth = lineW;
  ctx.strokeStyle = `rgba(${COLOR},0.85)`;
  ctx.shadowBlur = 8;
  ctx.shadowColor = `rgba(${COLOR},0.7)`;
  SKELETON_BONES.forEach(([a, b]) => {
    const pa = pose[a];
    const pb = pose[b];
    if (!pa || !pb) return;
    ctx.beginPath();
    ctx.moveTo(pa.x * W, pa.y * H);
    ctx.lineTo(pb.x * W, pb.y * H);
    ctx.stroke();
  });

  const { nose, leftShoulder: ls, rightShoulder: rs } = pose;
  if (nose && ls && rs) {
    const headR = Math.max(Math.abs(rs.x - ls.x) * W * 0.3, H * 0.02);
    ctx.beginPath();
    ctx.arc(nose.x * W, nose.y * H, headR, 0, Math.PI * 2);
    ctx.stroke();
  }

  ctx.fillStyle = `rgba(${COLOR},0.95)`;
  ctx.shadowBlur = 4;
  Object.values(pose).forEach((p) => {
    ctx.beginPath();
    ctx.arc(p.x * W, p.y * H, lineW * 1.6, 0, Math.PI * 2);
    ctx.fill();
  });
  ctx.restore();
}

/**
 * Полный скелет игрока на headless-ТВ (GEN-18912): виден в меню, на калибровке «встаньте в зону» и на экране итога,
 * скрыт, пока идёт сама игра (`isHidden`). Рисует позу из `BodyPoseStore`; без человека в кадре (`bodyPresent`) —
 * пусто, одиночная случайная точка скелетом не считается.
 */
export class HeadlessSkeletonOverlay {
  private canvas: HTMLCanvasElement | null = null;

  private ctx: CanvasRenderingContext2D | null = null;

  private raf = 0;

  private enabled = false;

  constructor(
    private readonly getPose: (nowMs: number) => BodyPose | null,
    /** `true` — идёт игра: скелет не показываем. */
    private readonly isHidden: () => boolean,
  ) {}

  /** Headless-телефон подключён — рисуем; иначе слой не нужен вовсе. */
  setEnabled(enabled: boolean): void {
    if (this.enabled === enabled) return;
    this.enabled = enabled;
    if (enabled) {
      if (!this.raf) this.raf = requestAnimationFrame(this.tick);
    } else {
      this.stopLoop();
      if (this.canvas) this.canvas.style.display = 'none';
    }
  }

  dispose(): void {
    this.setEnabled(false);
    this.canvas?.remove();
    this.canvas = null;
    this.ctx = null;
  }

  private stopLoop(): void {
    cancelAnimationFrame(this.raf);
    this.raf = 0;
  }

  private ensureCanvas(): CanvasRenderingContext2D | null {
    if (this.ctx) return this.ctx;
    const canvas = document.createElement('canvas');
    canvas.style.cssText = `position:fixed;inset:0;width:100%;height:100%;z-index:${Z_INDEX};pointer-events:none;`;
    document.body.appendChild(canvas);
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    return this.ctx;
  }

  private readonly tick = (now: number): void => {
    this.raf = 0;
    if (!this.enabled) return;
    this.raf = requestAnimationFrame(this.tick);
    const pose = this.isHidden() ? null : this.getPose(now);
    if (!pose || !bodyPresent(pose)) {
      if (this.canvas && this.canvas.style.display !== 'none') {
        this.canvas.style.display = 'none';
      }
      return;
    }
    const ctx = this.ensureCanvas();
    const { canvas } = this;
    if (!ctx || !canvas) return;
    canvas.style.display = 'block';
    const W = window.innerWidth || 1;
    const H = window.innerHeight || 1;
    if (canvas.width !== W || canvas.height !== H) {
      canvas.width = W;
      canvas.height = H;
    }
    drawPose(ctx, pose, W, H);
  };
}
